'use client';
import { useContext, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { LineItem } from 'shared/types/cart';
import { CartAndError } from 'shared/types/cart/Cart';
import { Money } from 'shared/types/product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { OrderLevelPPM } from '@/components/customercenter-ui/checkout/common/orderLevelPPM';
import CustomNarvarInsurance from '@/components/customercenter-ui/checkout/common/shippingInsurance/addRemoveShippingInsurance';
import WorryFreeDeliveryModal from '@/components/customercenter-ui/checkout/common/shippingInsurance/worryFreeDeliveryModal';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import ToolTips from '@/components/customercenter-ui/tooltip';
import SalesTax from '@/components/customercenter-ui/tooltip/salesTax';
import ShippingSurcharge from '@/components/customercenter-ui/tooltip/surCharge';
import { useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import ExcludedBrandsDialog from '@/customercenter-ui/components/product/details/components/ExcludedBrandsDialog';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { CartHelpers } from '@/helpers/utils/cartHelpers';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { createUrl } from '@/helpers/utils/siteUtils';
import { COOKIE_KEYS, ORDER_TYPES } from '../../../../../../constants/constant';
import EstimatedShipping from '../../../../tooltip/estimatedShipping';
import { RESPONSE_SUCCESS_MESSAGE } from '../../../customer-detail/constants';
import CancelOrderModal from '../../../modals/orderDetails/cancelOrder';
import HoldOrderModal from '../../../modals/orderDetails/holdOrder';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import Loader from '../../../ui/loader';
import { useToast } from '../../../ui/use-toast';
import { CANCEL_ORDER_EVENT_TYPES, CANCEL_ORDER_MESSAGE, CART_STATE_ACTIVE } from '../../constants';

export interface Props {
  setIsPromoCodeApplySuccess?: (value: boolean) => void;
  Texts?: any;
  widgetData?: any;
  orderData?: any;
  pageName?: string;
}

const CustomerOrderSummary: React.FC<Props> = ({
  setIsPromoCodeApplySuccess,
  widgetData,
  orderData = null,
  Texts,
  pageName,
}: any) => {
  const { toast } = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const { data: swrCart, unfreezeCart, beginNoChargeOrder, redeemDiscountCode, removeDiscountCode } = useCart(false);
  const { setCustomerDetailsResponse, contextCart } = useCheckout();
  const { setCustomerData, setCustomerDetailsResponse: setCreditSummaryDetailsResponse } =
    useContext(CreditSummaryContext);
  const { getCustomerDetails } = useAccount();
  const projectSettings: ProjectSettings = useProjSettings();
  const [discountApplied, setDiscountApplied] = useState(false);
  const [isEmptyPromoCode, setIsEmptyPromoCode] = useState(false);
  const data = pageName === 'checkout' ? contextCart ?? swrCart : swrCart;
  const CartData = orderData ?? data;
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invalidPromocode, setInvalidPromocode] = useState(false);
  const [discountGreaterThanZero, setDiscountGreaterThanZero] = useState<boolean>(
    (CartData?.discountTotal?.centAmount ?? 0) > 0,
  );
  const [adjustmentAmount, setAdjustmentAmount] = useState({
    centAmount: 0,
    currencyCode: 'USD',
    fractionDigits: 2,
    type: 'centPrecision',
  });
  const [promoCode, setPromoCode] = useState<string>(CartData?.discountCodes?.[0]?.code ?? '');
  const [userEnteredCode, setUserEnteredCode] = useState<string>();
  const [promoCodeOrder, setPromoCodeOrder] = useState({ code: '', description: '' });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  const [isPromoApplied, setIsPromoApplied] = useState(false);
  const [isCartHold, setIscartHold] = useState(false);
  const [releaseSuccessMsg, setReleaseSuccessMsg] = useState(false);
  const [holdSuccessMsg, setHoldSuccessMsg] = useState(false);
  const [cookieValue, setCookieValue] = useState<any>();
  const isNarvarEnabledForMailOrder = projectSettings?.isNarvarEnabledForMailOrder;
  const isNoChargeOrder =
    CartData?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE || CartData?.orderType === ORDER_TYPES.NO_CHARGE;
  const isMailOrder =
    (CartData?.additionalDetails?.orderType === ORDER_TYPES.MAIL_ORDER ||
      CartData?.orderType === ORDER_TYPES.MAIL_ORDER) &&
    CartData?.lineItems?.every((item: any) => item.productKey === '999999');
  const isMailOrderCart =
    CartData?.additionalDetails?.orderType === ORDER_TYPES.MAIL_ORDER || CartData?.orderType === ORDER_TYPES.MAIL_ORDER;
  const cookie = cookieValue ? JSON.parse(cookieValue) : {};
  const checkoutURL = '/checkout/' + createUrl(cookie?.customerId, cookie?.omsCustomerId, cookie?.divisionCode);
  const onThankyouPage = pathname?.includes('thank-you');
  const onCheckoutPage = pathname?.includes('checkout');
  const onCartPage = pathname?.includes('cart');
  const adjustmentShippingDiscount = CartData?.currentAdjustment?.filter(
    (item: { type: string }) => item.type === 'shippingDiscount',
  )[0];
  const adjustmentType = CartData?.currentAdjustment?.filter(
    (item: { type: string }) => item.type === 'absolute' || 'relative',
  );

  const shippingAmount = CartHelpers.calculateShippingAmount(
    CartData?.actualShippingAmount,
    CartData?.shippingDiscount,
    adjustmentShippingDiscount,
  );

  const formattedCheckAmount =
    CartData?.paymentDetails?.checkAmount != null ? Number(CartData.paymentDetails.checkAmount).toFixed(2) : '0.00';

  const orderTotal = (CartData?.total?.centAmount ?? 0) / 100;
  const insuranceLineItem: LineItem | undefined = CartData?.lineItems?.find((item: LineItem) => item?.isInsuranceItem);
  const insuranceItemPrice = insuranceLineItem?.price;
  let cartSubTotalPrice = CartData?.subTotal;
  if (insuranceItemPrice) {
    cartSubTotalPrice = CurrencyHelpers.subtractCurrency(CartData?.subTotal as Money, insuranceItemPrice);
  }
  const isCheckoutPage = onCheckoutPage && !onThankyouPage;
  const isCartOrCheckout = onCartPage || isCheckoutPage;
  let displayShippingInsuranceWidget = true;
  if (
    !CartData ||
    !isCartOrCheckout ||
    onThankyouPage ||
    (isMailOrderCart && !isNarvarEnabledForMailOrder) ||
    isNoChargeOrder
  ) {
    displayShippingInsuranceWidget = false;
  }
  const displayShippingInsuranceAmount = (onCartPage || onCheckoutPage || onThankyouPage) ?? false;
  useEffect(() => {
    const cookieValue = getCookie(COOKIE_KEYS.CDP_URL);
    setCookieValue(cookieValue);
  }, []);

  useEffect(() => {
    if (CartData?.discountCodes?.[0]?.discountCode?.obj?.code && CartData?.discountCodes?.[0]?.state !== 'NotActive') {
      setPromoCodeOrder({
        code: CartData?.discountCodes?.[0]?.discountCode?.obj?.code ?? '',
        description: CartData?.discountCodes?.[0]?.discountCode?.obj?.description?.['en-US'] ?? '',
      });
      setIsPromoApplied(true);
    }

    let adjAmount = 0;
    setDiscountGreaterThanZero((CartData?.discountTotal?.centAmount ?? 0) > 0);
    const currentAdjustment = CartData?.currentAdjustment;

    if (currentAdjustment && Array.isArray(currentAdjustment) && currentAdjustment.length > 0) {
      const shippingDiscounts = currentAdjustment?.filter((adj: any) => adj.type === 'shippingDiscount');

      adjAmount = currentAdjustment?.at(-1)?.centAmount - (shippingDiscounts?.at(-1)?.discountedAmount ?? 0);
    }

    if (currentAdjustment?.length > 0) {
      setAdjustmentAmount({
        centAmount: adjAmount,
        currencyCode: 'USD',
        fractionDigits: 2,
        type: 'centPrecision',
      });
    } else {
      adjAmount = 0;
      setAdjustmentAmount({
        centAmount: 0,
        currencyCode: 'USD',
        fractionDigits: 2,
        type: 'centPrecision',
      });
    }

    if (CartData?.cartState === CART_STATE_ACTIVE) {
      setIscartHold(false);
    } else {
      setIscartHold(true);
    }
  }, [CartData]);

  const handleUnFreezeCart = async () => {
    setLoading(true);
    await unfreezeCart();
    setLoading(false);
    setReleaseSuccessMsg(true);
    setTimeout(() => {
      setReleaseSuccessMsg(false);
    }, 5000);
  };

  const handleCheckout = async () => {
    setLoading(true);
    const customerDetailsResponse = await getCustomerDetails({
      query: {
        customerId: cookie?.customerId,
        omsCustomerId: cookie?.omsCustomerId ?? '',
        divisionCode: cookie?.divisionCode ?? '',
        idName: 'customerId',
      },
    });
    if (customerDetailsResponse?.code?.toUpperCase() === RESPONSE_SUCCESS_MESSAGE) {
      setCustomerDetailsResponse(customerDetailsResponse);
      setCustomerData(customerDetailsResponse?.Customer);
      setCreditSummaryDetailsResponse(customerDetailsResponse);
    }

    if (isNoChargeOrder) {
      await beginNoChargeOrder();
    }

    router.push(checkoutURL);
  };

  let discountCode = CartData?.discountCodes?.find((discountCode: { code: any }) => {
    return discountCode?.code;
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(event?.target?.value?.toUpperCase()?.trim());
    setUserEnteredCode(event?.target?.value?.toUpperCase()?.trim());
  };

  const handleKeyDown = async (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await applyPromoCode();
    }
  };

  useEffect(() => {
    discountCode = CartData?.discountCodes?.find((discountCode: { code: any }) => {
      return discountCode?.code;
    });
    if (discountCode?.code) {
      setPromoCode(discountCode?.code ?? '');
    } else {
      setPromoCode(userEnteredCode ?? '');
    }
    if (discountApplied) {
      if (CartData.discountCodes && CartData.discountCodes.length > 0) {
        if (CartData?.displayExcludedBrandMessage) {
          SuccessNotification(toast, {
            description: `${mapLocaleToMeaningfulFormat().promoCode} <b>${promoCode}</b>  ${
              mapLocaleToMeaningfulFormat().hasAppliedToOrderWithExcludedItems
            }`,
            action: <SuccessIcon className="h-5 w-5" />,
          });
        } else {
          SuccessNotification(toast, {
            description: `${mapLocaleToMeaningfulFormat().promoCode} <b>${promoCode}</b>  ${
              mapLocaleToMeaningfulFormat().hasAppliedToOrder
            }`,
            action: <SuccessIcon className="h-5 w-5" />,
          });
        }

        if (setIsPromoCodeApplySuccess) {
          setIsPromoCodeApplySuccess?.(true);
        }
      }
      setDiscountApplied(false);
    }
  }, [CartData]);

  const applyPromoCode = async () => {
    try {
      const cartAndError: CartAndError = await redeemDiscountCode(promoCode);
      if (cartAndError?.errorMessage) {
        setDiscountApplied(false);
        if (cartAndError?.errorMessage?.includes(mapLocaleToMeaningfulFormat().promoBrandExclusion)) {
          ErrorNotification(toast, {
            description: `${mapLocaleToMeaningfulFormat().promoCode} <b> ${promoCode} </b>  ${
              mapLocaleToMeaningfulFormat().promoBrandExclusion
            }`,
            action: <ErrorIcon className="h-5 w-5" />,
          });
        } else {
          ErrorNotification(toast, {
            description: `${mapLocaleToMeaningfulFormat().promoCode} <b> ${promoCode} </b>  ${
              mapLocaleToMeaningfulFormat().promoInvalid
            }`,
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }

        setPromoCode(userEnteredCode ?? '');
      } else {
        setDiscountApplied(true);
      }
    } catch (error) {
      setDiscountApplied(false);
      setPromoCode(userEnteredCode ?? '');
      ErrorNotification(toast, {
        description: `${mapLocaleToMeaningfulFormat().promoCode} <b> ${promoCode} </b> ${
          mapLocaleToMeaningfulFormat().promoInvalid
        }`,
        action: <ErrorIcon className="h-5 w-5" />,
      });
    }
    window.scrollTo(0, 0);
  };

  const removePromoCode = async () => {
    if (CartData?.discountCodes != undefined) {
      try {
        const discountCode = CartData?.discountCodes?.find((discountCode: { code: any }) => {
          return discountCode?.code;
        });
        if (discountCode) {
          setDiscountApplied(false);
          await removeDiscountCode(discountCode);
        }
        ErrorNotification(toast, {
          description: `${mapLocaleToMeaningfulFormat().promoCode} <b> ${promoCode} </b> ${
            mapLocaleToMeaningfulFormat().hasRemovedFromOrder
          }`,
          action: <ErrorIcon className="h-5 w-5" />,
        });
        setPromoCode('');
      } catch (error) {
        setDiscountApplied(false);
        ErrorNotification(toast, {
          description: `${mapLocaleToMeaningfulFormat().promoCode} <b> ${promoCode} </b> ${
            mapLocaleToMeaningfulFormat().hasRemovedFromOrder
          }`,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    }
    window.scrollTo(0, 0);
  };

  return (
    <Card className="rounded-md border-[1px] border-foggyGray bg-lightSilver shadow-lg">
      <CardHeader className="px-5 py-3">
        <CardTitle className="pt-1.5 text-lg font-bold">{Texts.orderSummary}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">
        {loading && <Loader />}
        <ul className="pb-3">
          <li className="mb-2 flex justify-between text-base font-normal">
            <p>
              {Texts.subtotal} (
              {CartData?.lineItems?.reduce((currentQuantity: number, item: any) => {
                // dont include warranty items to total count
                if (!item.lineGroupId && !item.isInsuranceItem) {
                  return currentQuantity + (item.count || 0);
                }
                return currentQuantity;
              }, 0)}{' '}
              {Texts.items}):
            </p>
            {CartData?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE ||
            orderData?.orderType === ORDER_TYPES.NO_CHARGE ? (
              '$0.00'
            ) : (
              <p>{CurrencyHelpers.customCurrencyFormat(cartSubTotalPrice || {})}</p>
            )}
          </li>

          {discountGreaterThanZero ? (
            <li className="mb-2 flex justify-between text-base font-normal">
              <section className="flex items-center">{Texts.promotions}:</section>

              <p className="text-scarletRed">-{CurrencyHelpers.formatForCurrency(CartData?.discountTotal as Money)}</p>
            </li>
          ) : (
            <></>
          )}

          {adjustmentType && adjustmentAmount?.centAmount > 0 && (
            <li className="mb-2 flex justify-between text-base font-normal">
              <p>{Texts.adjustments}:</p>
              <p className="text-scarletRed">-{CurrencyHelpers.customCurrencyFormat(adjustmentAmount)}</p>
            </li>
          )}
          <li className="mb-2 flex justify-between text-base font-normal">
            <section className="flex items-center">
              {Texts.estimatedShipping}:{' '}
              <ToolTips
                className="ml-1 mt-1 h-4 w-4 text-[#B6B6B8] md:mt-2"
                Content={{ ContentToolTip: <EstimatedShipping /> }}
              />
            </section>
            {CartData?.lineItems?.length > 0 ? (
              <>
                {isNoChargeOrder
                  ? 'FREE'
                  : isMailOrder
                  ? CurrencyHelpers.customCurrencyFormat({ centAmount: 0 })
                  : CartData?.shippingDiscount?.centAmount && shippingAmount?.centAmount === 0
                  ? mapLocaleToMeaningfulFormat().free
                  : shippingAmount?.centAmount === 0 && CartData?.shippingInfo?.price?.centAmount
                  ? adjustmentShippingDiscount
                    ? CurrencyHelpers.customCurrencyFormat(shippingAmount)
                    : CurrencyHelpers.customCurrencyFormat(CartData?.shippingInfo?.price)
                  : CurrencyHelpers.customCurrencyFormat(shippingAmount)}
              </>
            ) : (
              CurrencyHelpers.customCurrencyFormat({ centAmount: 0 })
            )}
          </li>
          <li className="mb-2 flex justify-between text-base font-normal">
            <section className="flex items-center">
              {Texts.shippingSurcharge}:{' '}
              <ToolTips
                className="ml-1 mt-1 h-4 w-4 text-[#B6B6B8] md:mt-2"
                Content={{ ContentToolTip: <ShippingSurcharge /> }}
              />
            </section>
            <p>{CurrencyHelpers.customCurrencyFormat(CartData?.shippingSurcharge ?? { centAmount: 0 })}</p>
          </li>
          {insuranceItemPrice?.centAmount && displayShippingInsuranceAmount && (
            <li className="mb-2 flex justify-between text-base font-normal">
              <section className="flex items-center">
                <div className="pr-1">{`${mapLocaleToMeaningfulFormat().worryFreeDelivery}: `} </div>
                <WorryFreeDeliveryModal />
              </section>
              <p className="text-base font-normal leading-[22.2px] text-black">
                {' '}
                {CurrencyHelpers.formatForCurrency(
                  (insuranceItemPrice as Money) ?? ({ costInCents: 0, fractionDigits: 2 } as Money),
                )}
              </p>
            </li>
          )}

          <li className="mb-2 flex justify-between text-base font-normal">
            <section className="flex items-center">
              {Texts.estimatedSalesTax}:{' '}
              <ToolTips className="ml-1 mt-1 h-4 w-4 text-[#B6B6B8]" Content={{ ContentToolTip: <SalesTax /> }} />
            </section>
            <p>{CurrencyHelpers.customCurrencyFormat(CartData?.totalTaxedPrice ?? { centAmount: 0 })}</p>
          </li>
          <li className="my-4 flex justify-between text-base font-bold">
            <p>{Texts.orderTotal}:</p>
            <p>
              {CartData?.lineItems?.length === 0 ||
              CartData?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE ||
              orderData?.orderType === ORDER_TYPES.NO_CHARGE ||
              isMailOrder
                ? '$0.00'
                : CurrencyHelpers.customCurrencyFormat(CartData?.total || {})}
            </p>
          </li>
          {onThankyouPage && CartData?.paymentType === 'check' && (
            <li className="my-4 flex justify-between text-base font-bold">
              <p>Check Amount:</p>
              <p>${formattedCheckAmount}</p>
            </li>
          )}
          {orderTotal > 0 &&
            projectSettings?.enableOrderLevelPPM &&
            cookie?.divisionCode !== 'MQ' &&
            cookie?.divisionCode !== 'SM' && (
              <li>
                <OrderLevelPPM page={pageName} orderTotal={orderTotal} isLoggedIn={true} />
              </li>
            )}
        </ul>
        {!orderData && (
          <>
            <div className="border-t">
              {!discountCode?.code ? (
                <div className="my-2 flex">
                  <p className="text-base font-bold">{Texts.applyPromoCode}</p>&nbsp;
                  <span> ({Texts.optional})</span>
                </div>
              ) : (
                <></>
              )}
              {!discountCode?.code ? (
                <div>
                  <section className="flex">
                    <Input
                      autoCapitalize="characters"
                      type="text"
                      value={promoCode}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      id="promo-code"
                      className={`border-1 gbunmask h-[45px] ${
                        invalidPromocode ? 'border-scarletRed text-scarletRed' : 'border-foggyGray text-black'
                      } placeholder:text-foggyGray focus:outline-none`}
                      onClick={() => setIsEmptyPromoCode(false)}
                      aria-labelledby="promo-code-label"
                      placeholder="Enter Promo Code"
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (promoCode) {
                          await applyPromoCode();
                        } else {
                          setIsEmptyPromoCode(true);
                        }
                      }}
                      className="ml-2 h-[45px] border-[1px] border-foggyGray bg-neutral-100 px-3 py-1.5 text-xs font-medium"
                    >
                      {mapLocaleToMeaningfulFormat().applyCode}
                    </Button>
                  </section>
                  {invalidPromocode ? <h3 className="my-3 text-scarletRed">{error}</h3> : <></>}
                  {isEmptyPromoCode && (
                    <p className="mt-1 pl-1 font-semibold text-red-700">
                      {mapLocaleToMeaningfulFormat().emptyPromoCode}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <div className="">
                      <span className="text-base font-bold text-scarletRed">{CartData?.discountCodes?.[0]?.code}</span>
                      <span>&nbsp;{Texts.applied}</span>
                    </div>
                    <Button
                      onClick={() => {
                        removePromoCode();
                      }}
                      className="m-0 p-0 font-medium text-[#0075C9] underline"
                    >
                      {Texts.remove}
                    </Button>
                  </div>
                  <div className="font-medium">{CartData?.discountCodes?.[0]?.description}</div>
                </div>
              )}

              {displayShippingInsuranceWidget && (
                <CustomNarvarInsurance cart={CartData} isCheckoutPage={isCheckoutPage} />
              )}
              {!onCheckoutPage && (
                <>
                  {' '}
                  <ExcludedBrandsDialog widgetData={widgetData?.widgetData} />{' '}
                  <Button
                    className="mt-4 w-full bg-deepBlue py-7 text-xl font-bold text-white disabled:opacity-50"
                    onClick={handleCheckout}
                    disabled={isCartHold}
                  >
                    {Texts.checkOut}
                  </Button>
                </>
              )}
            </div>
            <div className="mt-4 flex items-center justify-center gap-3 text-base font-normal ">
              {!isCartHold ? (
                <HoldOrderModal Texts={Texts} setHoldSuccessMsg={setHoldSuccessMsg} />
              ) : (
                <Button
                  className="h-auto p-0 text-base font-normal text-blueLight underline"
                  onClick={handleUnFreezeCart}
                >
                  {Texts.releaseCart}
                </Button>
              )}
              <div>|</div>
              <CancelOrderModal
                Texts={Texts}
                className="text-blueLight underline"
                CancelEvent={CANCEL_ORDER_EVENT_TYPES.ENTIRE_ORDER}
                message={CANCEL_ORDER_MESSAGE.OPREATOR_CHANGE}
                comercetoolsOrder
              ></CancelOrderModal>
            </div>

            {releaseSuccessMsg && (
              <p className="mt-3 text-center font-bold text-green-800">Order Hold Released Successfully</p>
            )}
            {holdSuccessMsg && <p className="mt-3 text-center font-bold text-green-800">Order Hold Successfully</p>}
          </>
        )}
        {orderData && isPromoApplied && (
          <>
            <div className="border-t">
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <div className="">
                    <span className="text-base font-bold text-scarletRed">{promoCodeOrder?.code}</span>
                    <span>&nbsp;{Texts.applied}</span>
                  </div>
                </div>
                <div className="font-medium">{promoCodeOrder?.description}</div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerOrderSummary;
